import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useCallback } from 'react'
import useSWR from 'swr'

import type {
  LabelerPageMeta,
  LabelerPrediction,
  LabelerRun,
  LabelerSiteStats,
  LabelerTaxonomy,
  PageLabels,
  SourceListSummary,
  VerdictSummary,
} from './types'

const API_PREFIX = '/api/v1'

export interface PublishManualListArgs {
  site: string
  name?: string
  labeledPages: string[]
  inProgress: boolean
  recordsDocument: { records: unknown[] }
}

export interface PutVerdictsArgs {
  site: string
  pages: Array<{
    page_id: string
    document_id: string
    page_num: number
    run_id: string | null
    complete: boolean
    predictions: LabelerPrediction[]
    boxes: PageLabels['boxes']
    rejections: PageLabels['rejections']
  }>
}

export function useLabelerService() {
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()

  const labelerBase = useCallback(
    (site?: string): string => {
      if (!site) throw new Error('labeler request requires a site')
      return `${FIM_SERVICE_URL}${API_PREFIX}/sites/${encodeURIComponent(site)}/labeler`
    },
    [FIM_SERVICE_URL]
  )

  const sourcesBase = useCallback(
    (site?: string): string => {
      if (!site) throw new Error('labeler request requires a site')
      return `${FIM_SERVICE_URL}${API_PREFIX}/sites/${encodeURIComponent(site)}/sources`
    },
    [FIM_SERVICE_URL]
  )

  const getJson = useCallback(
    async <T>(url: string): Promise<T> => {
      const res = await authorizedAxios.get(url)
      return res.data as T
    },
    [authorizedAxios]
  )

  /** PUT /sources/manual — upsert the caller's per-(site,user) manual list. */
  const publishManualList = useCallback(
    async (args: PublishManualListArgs): Promise<SourceListSummary> => {
      const body = new FormData()
      if (args.name) body.append('name', args.name)
      body.append(
        'params_json',
        JSON.stringify({
          labeled_pages: args.labeledPages,
          in_progress: args.inProgress,
        })
      )
      body.append(
        'file',
        new Blob([JSON.stringify(args.recordsDocument)], {
          type: 'application/json',
        }),
        'manual_labels.json'
      )
      const res = await authorizedAxios.put(
        `${sourcesBase(args.site)}/manual`,
        body,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return res.data as SourceListSummary
    },
    [authorizedAxios, sourcesBase]
  )

  /** PUT /labeler/verdicts — the sidecar audit entry (per site+user). */
  const putVerdicts = useCallback(
    async (args: PutVerdictsArgs): Promise<VerdictSummary> => {
      const res = await authorizedAxios.put(
        `${labelerBase(args.site)}/verdicts`,
        { pages: args.pages }
      )
      return res.data as VerdictSummary
    },
    [authorizedAxios, labelerBase]
  )

  const listSourceLists = useCallback(
    async (site: string): Promise<SourceListSummary[]> => {
      const res = await authorizedAxios.get(sourcesBase(site))
      return res.data as SourceListSummary[]
    },
    [authorizedAxios, sourcesBase]
  )

  const listRecords = useCallback(
    async (
      site: string,
      listId: string
    ): Promise<{ total: number; records: unknown[] }> => {
      const res = await authorizedAxios.get(
        `${sourcesBase(site)}/${encodeURIComponent(listId)}/records?limit=10000`
      )
      return res.data as { total: number; records: unknown[] }
    },
    [authorizedAxios, sourcesBase]
  )

  return {
    labelerBase,
    sourcesBase,
    getJson,
    publishManualList,
    putVerdicts,
    listSourceLists,
    listRecords,
  }
}

function useLabelerGet<T>(site: string | undefined, path: string | null) {
  const { labelerBase, getJson } = useLabelerService()
  const key = site && path !== null ? `${labelerBase(site)}${path}` : null
  return useSWR<T>(key, getJson, { revalidateOnFocus: false })
}

export function useLabelerTaxonomy(site: string | undefined) {
  return useLabelerGet<LabelerTaxonomy>(site, '/taxonomy')
}

export function useLabelerSiteStats(site: string | undefined) {
  return useLabelerGet<LabelerSiteStats>(site, '/stats')
}

export function useLabelerPage(
  site: string | undefined,
  pageId: string | undefined
) {
  return useLabelerGet<LabelerPageMeta>(
    site,
    pageId ? `/pages/${encodeURIComponent(pageId)}` : null
  )
}

export function useLabelerPredictions(
  site: string | undefined,
  pageId: string | undefined,
  runId: string | null
) {
  const runParam = runId ? `?run_id=${encodeURIComponent(runId)}` : ''
  return useLabelerGet<LabelerPrediction[]>(
    site,
    pageId
      ? `/pages/${encodeURIComponent(pageId)}/predictions${runParam}`
      : null
  )
}

export function useLabelerPageOcr(
  site: string | undefined,
  pageId: string | undefined
) {
  return useLabelerGet<import('./types').LabelerOcrBlock[]>(
    site,
    pageId ? `/pages/${encodeURIComponent(pageId)}/ocr` : null
  )
}

export function useLabelerPageRuns(
  site: string | undefined,
  pageId: string | undefined
) {
  return useLabelerGet<LabelerRun[]>(
    site,
    pageId ? `/pages/${encodeURIComponent(pageId)}/runs` : null
  )
}

export function useLabelerVerdictSummaries(site: string | undefined) {
  return useLabelerGet<Array<VerdictSummary & { entry_id: string }>>(
    site,
    '/verdicts'
  )
}

export function useSiteSourceLists(site: string | undefined) {
  const { sourcesBase, getJson } = useLabelerService()
  return useSWR<SourceListSummary[]>(site ? sourcesBase(site) : null, getJson, {
    revalidateOnFocus: false,
  })
}

/** One page's slice of a source list, filtered server-side — a site-scale
 * list cannot be paged through client-side. */
export function useSourceListPageRecords(
  site: string | undefined,
  listId: string | null,
  pageId: string | undefined
) {
  const { sourcesBase, getJson } = useLabelerService()
  const key =
    site && listId && pageId
      ? `${sourcesBase(site)}/${encodeURIComponent(listId)}/records?limit=10000&page_id=${encodeURIComponent(pageId)}`
      : null
  return useSWR<{ total: number; records: unknown[] }>(key, getJson, {
    revalidateOnFocus: false,
  })
}
